<template>
  <div class="cre-view">
    <div
      v-if="isInitializing"
      class="loading"
    >
      <unc-loader />
    </div>

    <template v-else-if="showNotFound">
      <div class="register-card cre-card rr-notfound-card">
        <div class="rr-notfound-inner">
          <div class="rr-notfound-icon">
            <svg
              aria-hidden="true"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              />
              <line
                x1="12"
                y1="8"
                x2="12"
                y2="12"
              />
              <line
                x1="12"
                y1="16"
                x2="12.01"
                y2="16"
              />
            </svg>
          </div>
          <h1 class="rr-notfound-title">
            No se pudo actualizar tu correo de recuperación
          </h1>
          <p class="rr-notfound-desc">
            El enlace utilizado caducó o no es válido. Solicitá un nuevo enlace para cambiar tu correo de recuperación al administrador de tu dependencia o facultad.
          </p>

          <div class="rr-actions rr-actions--center">
            <button
              type="button"
              class="btn btn-primary waves-effect register-btn register-btn-primary rr-notfound-btn"
              style="width: 300px;"
              @click.prevent="goToContacts()"
            >
              <span>Ver listado de administradores</span>
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="register-card cre-card">
        <div class="register-glow" />
        <div class="card-header ch-alt register-header">
          <div class="register-kicker rr-kicker--success">
            Operación exitosa
          </div>
          <h1 class="register-title">
            Correo actualizado
          </h1>
          <p class="register-subtitle">
            Tu correo de recuperación fue actualizado satisfactoriamente.
          </p>
        </div>
        <div class="register-body">
          <div class="cre-recs">
            <div class="cre-recs-label">
              Recomendaciones
            </div>
            <ul class="cre-recs-list">
              <li>
                Si recordás tu contraseña, podés iniciar sesión haciendo click en <strong>Iniciar sesión en
                  MiUNC</strong>.
              </li>
              <li>
                ¿No recordás tu contraseña? Para recuperarla hacé click en <strong>Recuperar contraseña</strong> y
                seguí el procedimiento indicado.
              </li>
            </ul>
          </div>

          <div class="rr-actions">
            <a
              :href="configApp.urlRestorePass"
              target="_blank"
              rel="noopener"
              class="register-btn register-btn-secondary"
            >
              Recuperar contraseña
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect
                  x="3"
                  y="11"
                  width="18"
                  height="11"
                  rx="2"
                  ry="2"
                />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span class="visually-hidden"> (se abre en una pestaña nueva)</span>
            </a>
            <a
              :href="configApp.urlMiUNC"
              target="_blank"
              rel="noopener"
              class="register-btn register-btn-primary"
            >
              Iniciar sesión en MiUNC
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line
                  x1="15"
                  y1="12"
                  x2="3"
                  y2="12"
                />
              </svg>
              <span class="visually-hidden"> (se abre en una pestaña nueva)</span>
            </a>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { HttpFRONT, swal } from '../../share.js';
import { mapGetters } from 'vuex';

export default {
    name: 'ConfirmRecoveryEmail',
    props: {
        token: {
            type: String,
            default: '',
        },
    },
    data () {
        return {
            isInitializing: true,
            showNotFound: false,
        };
    },
    computed: {
        ...mapGetters([
            'configApp',
        ]),
        showOneColumn () {
            return window.screen.width < 400;
        },
    },
    async mounted () {
        if (this.token) {
            await this.getData();
        } else {
            this.showNotFound = true;
            this.isInitializing = false;
        }
    },
    methods: {
        async getData () {
            try {
                const response = await HttpFRONT.get(`confirmRecoveryEmail/${this.token}`);
                this.isInitializing = false;
                if (response.status === 200) {
                    this.showNotFound = false;
                    swal({ title: 'Correo de recuperación actualizado', text: 'Tu correo de recuperación fue actualizado satisfactoriamente.', type: 'success' });
                } else {
                    this.showNotFound = true;
                }
            } catch (error) {
                const status = error.response?.status;
                const message = error.response?.data || 'Servicio temporalmente no disponible';

                switch (status) {
                case 400:
                case 404:
                    this.isInitializing = false;
                    this.showNotFound = true;
                    swal({ title: 'Algo salió mal', text: message, type: 'warning' });
                    break;
                case 409:
                    this.isInitializing = false;
                    swal({ title: 'Algo salió mal', text: message, type: 'warning' });
                    this.$router.push('/');
                    break;
                default:
                    this.isInitializing = false;
                    await swal({ title: 'Algo salió mal', text: message, type: 'warning' });
                    this.$router.push('/');
                    break;
                }
            }
        },
        goToContacts () {
            this.$router.push('/contactos');
        },
    },
};
</script>

<style lang="scss" scoped>
@import './register-shared.scss';

.cre-view {
  --register-azul: #003865;
  --register-azul-deep: #001f3f;
  --register-azul-mid: #0a5f96;
  --register-verde: #3eb6b8;
  --register-ink: #102238;
  --register-muted: #526579;
  --register-border: rgba(0, 56, 101, 0.14);

  font-family: var(--font-body);
  min-height: 60vh;
  padding: 32px 16px 48px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.cre-card {
  width: 100%;
  max-width: $register-card-wide;
}

.cre-view .rr-kicker--success {
  border: 1px solid rgba(29, 158, 126, 0.24);
  background: rgba(29, 158, 126, 0.1);
  color: #1d9e7e;
}

.cre-view .rr-kicker--danger {
  border: 1px solid rgba(192, 57, 43, 0.24);
  background: rgba(220, 53, 69, 0.1);
  color: #c0392b;
}

.cre-status-block {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  border-radius: 14px;
  margin-bottom: 8px;
}

.cre-status-block--danger {
  background: rgba(220, 53, 69, 0.07);
  border: 1px solid rgba(220, 53, 69, 0.18);
  color: #c0392b;
}

.cre-status-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.cre-status-msg {
  font-weight: 600;
  font-size: 14px;
  margin: 0 0 4px;
}

.cre-status-sub {
  font-size: 13px;
  margin: 0;
  opacity: 0.85;
}

.cre-recs {
  margin-bottom: 20px;
}

.cre-recs-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--register-azul-mid);
  margin-bottom: 10px;
}

.cre-recs-list {
  margin: 0;
  padding-left: 22px;
  color: var(--register-ink);
}

.cre-recs-list li {
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 6px;
}

.register-btn {
  width: 200px;
}

</style>
